import React, { useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, IconControl, JSONEditorControl, Loading } from "spark-core-dx/components";
import "./RecentLogsControl.scss";

const normalizeCompare = (value) => String(value ?? "").trim().toLowerCase();

const getValue = (item, ...keys) => {
  if (!item) return "";
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
};

const getString = (item, ...keys) => String(getValue(item, ...keys) ?? "").trim();

const getNumber = (item, ...keys) => {
  const value = getValue(item, ...keys);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getArray = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const firstItem = (items) => {
  if (!items) return null;
  if (typeof items.first === "function") return items.first();
  return Array.isArray(items) ? items[0] : null;
};

const getId = (item) => getString(item, "_id", "Id", "id", "UniqueId", "uniqueId");

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const LOG_LEVEL_LABELS = {
  0: "Debug",
  1: "Info",
  2: "Timing",
  3: "Warning",
  4: "Error"
};

const extractItems = (response) => {
  if (Array.isArray(response)) return response;
  const result = firstItem(response) || response;
  return getArray(result, "Items", "items");
};

const buildLogQuery = ({ logReferenceId, resourceConfigurationId, clientResourceId }) => {
  if (logReferenceId) {
    return {
      searchModel: "x => x.LogReferenceId == logReferenceId",
      params: { logReferenceId }
    };
  }

  if (resourceConfigurationId) {
    return {
      searchModel: "x => x.ResourceConfigurationId == resourceConfigurationId",
      params: { resourceConfigurationId }
    };
  }

  if (clientResourceId) {
    return {
      searchModel: "x => x.ClientResourceId == clientResourceId",
      params: { clientResourceId }
    };
  }

  return null;
};

const formatIntegrationType = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatEventType = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
};

const parseJsonDescription = (description) => {
  const raw = String(description ?? "").trim();
  if (!raw.startsWith("{") && !raw.startsWith("[")) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const LOG_PREVIEW_FIELD_PRIORITY = [
  "DisplayMessage",
  "displayMessage",
  "Message",
  "message",
  "Error",
  "error",
  "ErrorMessage",
  "errorMessage"
];

const normalizePreviewMessage = (value) =>
  String(value ?? "")
    .replace(/\\r\\n|\\n|\\r/g, " ")
    .replace(/\r?\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const parseJsonLikeValue = (value) => {
  if (typeof value !== "string") return null;
  return parseJsonDescription(value);
};

const findJsonMessageValue = (value, depth = 0) => {
  if (!value || depth > 6) return "";

  if (typeof value === "string") {
    const parsedValue = parseJsonLikeValue(value);
    return parsedValue ? findJsonMessageValue(parsedValue, depth + 1) : "";
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const candidate = findJsonMessageValue(item, depth + 1);
      if (candidate) return candidate;
    }
    return "";
  }

  if (typeof value !== "object") return "";

  for (const key of LOG_PREVIEW_FIELD_PRIORITY) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      const rawCandidate = value[key];
      const nestedCandidate = findJsonMessageValue(rawCandidate, depth + 1);
      const candidate = nestedCandidate || normalizePreviewMessage(rawCandidate);
      if (candidate && !candidate.startsWith("{") && !candidate.startsWith("[")) {
        return candidate;
      }
    }
  }

  for (const nestedValue of Object.values(value)) {
    const candidate = findJsonMessageValue(nestedValue, depth + 1);
    if (candidate) return candidate;
  }

  return "";
};

const formatJsonDescriptionPreview = (description) => {
  const parsedDescription = parseJsonDescription(description);
  if (!parsedDescription) return description;
  return findJsonMessageValue(parsedDescription) || description;
};

const formatConnectionCheckDescription = (log, title, description) => {
  const eventName = normalizeCompare(getString(log, "EventName", "eventName", "Location", "location", "Type", "type"));
  if (eventName !== "connectioncheck") return description;

  const parsedDescription = parseJsonDescription(description);
  const systemInfo = getString(log, "SystemInfo", "systemInfo");
  const parsedIntegrationType = parsedDescription?.IntegrationType ?? parsedDescription?.integrationType;
  const systemIntegrationType = systemInfo.match(/Integration Type:\s*(.+)$/i)?.[1];
  const integrationType = formatIntegrationType(parsedIntegrationType || systemIntegrationType);
  const failed = normalizeCompare(title).includes("failed")
    || normalizeCompare(parsedDescription?.Result ?? parsedDescription?.result).includes("fail");
  const prefix = failed ? "API connection failed" : "API connection successful";
  const target = integrationType ? ` for ${integrationType}` : "";

  if (!failed) {
    return `${prefix}${target}.`;
  }

  const failureMessage = String(
    parsedDescription?.Message
    ?? parsedDescription?.message
    ?? description
    ?? ""
  ).split(/\r?\n/)[0].trim();
  const suffix = failureMessage ? ` ${failureMessage}` : "";
  return `${prefix}${target}.${suffix}`;
};

const toLogEntries = (logs, take) =>
  logs
    .map((log, index) => {
      const timestamp = getString(
        log,
        "TimestampUtc",
        "timestampUtc",
        "Timestamp",
        "timestamp",
        "CreatedDate",
        "createdDate",
        "UpdatedDate",
        "updatedDate"
      );
      const logLevel = getNumber(log, "LogLevel", "logLevel");
      const type = formatEventType(getString(log, "EventName", "eventName", "Type", "type", "Level", "level", "EventType", "eventType", "Category", "category"))
        || LOG_LEVEL_LABELS[logLevel]
        || "Log";
      const messages = getArray(log, "Messages", "messages");
      const title = String(messages[0] ?? "").trim()
        || getString(log, "DisplayMessage", "displayMessage", "Title", "title", "Message", "message", "Name", "name")
        || type;
      const rawDescription = getString(log, "Description", "description", "Details", "details", "Summary", "summary", "StackTrace", "stackTrace", "SystemInfo", "systemInfo");
      const detailsDescription = rawDescription;
      const description = formatJsonDescriptionPreview(formatConnectionCheckDescription(log, title, rawDescription));

      return {
        id: getId(log) || `${type}-${timestamp}-${index}`,
        type,
        title,
        description,
        detailsDescription,
        timestamp
      };
    })
    .sort((left, right) => String(right.timestamp).localeCompare(String(left.timestamp)))
    .slice(0, take);

const RecentLogsControl = ({
  logManager,
  logReferenceId,
  resourceConfigurationId,
  clientResourceId,
  take,
  Take,
  refreshKey = 0,
  emptyMessage = "No recent logs yet.",
  className = ""
}) => {
  const logManagerRef = useRef(logManager);
  const resolvedTake = useMemo(() => {
    const parsed = Number(take ?? Take ?? 5);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 5;
  }, [Take, take]);
  const [state, setState] = useState({
    loading: Boolean(logReferenceId || resourceConfigurationId || clientResourceId),
    error: "",
    logs: []
  });
  const [selectedEntry, setSelectedEntry] = useState(null);
  const query = useMemo(() => buildLogQuery({
    logReferenceId,
    resourceConfigurationId,
    clientResourceId
  }), [clientResourceId, logReferenceId, resourceConfigurationId]);
  const queryKey = useMemo(() => JSON.stringify(query || {}), [query]);
  const hasLogManager = Boolean(logManager?.search);

  useEffect(() => {
    logManagerRef.current = logManager;
  }, [logManager]);

  useEffect(() => {
    let mounted = true;

    const loadLogs = async () => {
      if (!query) {
        setState({ loading: false, error: "", logs: [] });
        return;
      }

      const activeLogManager = logManagerRef.current;
      if (!activeLogManager?.search) {
        setState({ loading: false, error: "Log service is not available.", logs: [] });
        return;
      }

      setState((previous) => ({
        ...previous,
        loading: true,
        error: ""
      }));

      try {
        const response = await activeLogManager.search({
          ...query,
          take: resolvedTake
        });
        if (!mounted) return;
        setState({
          loading: false,
          error: "",
          logs: extractItems(response)
        });
      } catch (loadError) {
        if (!mounted) return;
        setState({
          loading: false,
          error: loadError?.message || "Recent logs could not be loaded.",
          logs: []
        });
      }
    };

    loadLogs();

    return () => {
      mounted = false;
    };
  }, [hasLogManager, query, queryKey, refreshKey, resolvedTake]);

  const entries = useMemo(() => {
    const expectedReferenceId = normalizeCompare(logReferenceId);
    const scopedLogs = expectedReferenceId
      ? state.logs.filter((log) => normalizeCompare(getString(log, "LogReferenceId", "logReferenceId")) === expectedReferenceId)
      : state.logs;

    return toLogEntries(scopedLogs, resolvedTake);
  }, [logReferenceId, resolvedTake, state.logs]);
  const selectedEntryJson = useMemo(
    () => parseJsonDescription(selectedEntry?.detailsDescription || selectedEntry?.description),
    [selectedEntry?.description, selectedEntry?.detailsDescription]
  );

  if (state.loading) {
    return (
      <div className={["recent-logs-control", "recent-logs-control--loading", className].filter(Boolean).join(" ")}>
        <Loading type="spinner-2" size="3rem" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={["recent-logs-control", "recent-logs-control--error", className].filter(Boolean).join(" ")} role="alert">
        <IconControl iconKey="warning" width="2.8rem" height="2.8rem" />
        <p>{state.error}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className={["recent-logs-control", "recent-logs-control--empty", className].filter(Boolean).join(" ")}>
        <IconControl iconKey="logscreen" width="2.8rem" height="2.8rem" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={["recent-logs-control", className].filter(Boolean).join(" ")}>
      <div className="recent-logs-control__list">
        {entries.map((entry) => (
          <article key={entry.id} className="recent-logs-control__row">
            <span>{entry.type}</span>
            <strong>{entry.title}</strong>
            {entry.description ? (
              <div className="recent-logs-control__description-row">
                <p title={entry.detailsDescription || entry.description}>{entry.description}</p>
                <ButtonControl
                  type="standard-small"
                  className="recent-logs-control__details-button"
                  title={`View details for ${entry.title}`}
                  aria-label={`View details for ${entry.title}`}
                  onClick={() => setSelectedEntry(entry)}
                >
                  <IconControl iconKey="info" title={`View details for ${entry.title}`} width="1.9rem" height="1.9rem" />
                </ButtonControl>
              </div>
            ) : null}
            {entry.timestamp ? <small>{formatDateTime(entry.timestamp)}</small> : null}
          </article>
        ))}
      </div>
      <DialogControl
        openDialog={Boolean(selectedEntry)}
        title="Log Details"
        okText="Close"
        onOk={() => setSelectedEntry(null)}
        onCancel={() => setSelectedEntry(null)}
        onX={() => setSelectedEntry(null)}
        excludeCancel={true}
      >
        <div className="recent-logs-control__details-dialog">
          {selectedEntry?.type ? <span>{selectedEntry.type}</span> : null}
          {selectedEntry?.title ? <strong>{selectedEntry.title}</strong> : null}
          {selectedEntry?.timestamp ? <small>{formatDateTime(selectedEntry.timestamp)}</small> : null}
          {selectedEntryJson ? (
            <div className="recent-logs-control__json-viewer">
              <JSONEditorControl
                metadata={{ FieldName: "LogDetails", DisplayName: "Log Details" }}
                value={selectedEntryJson}
                onChange={() => {}}
              />
            </div>
          ) : (
            <pre>{selectedEntry?.detailsDescription || selectedEntry?.description || "No details are available."}</pre>
          )}
        </div>
      </DialogControl>
    </div>
  );
};

export default RecentLogsControl;
